import { ContactUsSchemaType } from "@/schemas/contact-us";
import { useTrans } from "@/utils/useTrans";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/v2-components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/v2-components/ui/select";
import { useFormContext } from "react-hook-form";
import ChevronDownIcon from "@/v2-components/core/icon/chevron-down-icon";
import { Fragment, useEffect } from "react";
import { cn } from "@/libs/utils";
import { useContactUsEmailStorage } from "@/components/support/contactUs/email/contact-us-email-storage";

export const getDefaultDescription = (category: string, topic: string) => {
  const base = `Please describe the issue in detail\n\n`;
  if (
    category.startsWith("device") &&
    (topic === "pair-connect" ||
      topic === "motor-vibration" ||
      topic === "charging")
  ) {
    return base.concat(
      `Please provide these information: \nSerial Number: \nFirmware Version: \n`,
    );
  }

  if (
    category === "order" &&
    (topic === "order-tracking-information" || topic === "shipping-information")
  ) {
    return base.concat(`Invoice Number: \nCountry: \n`);
  }

  if (
    category === "order" &&
    (topic === "request-refund" ||
      topic === "repair" ||
      topic === "cancellation")
  ) {
    return base.concat(`Invoice Number: \nReasons for return: \n`);
  }

  if (
    category === "game-software" &&
    (topic === "pair-connect" || topic === "haptic-feedback" || topic === "sdk")
  ) {
    return base.concat(`App Version: \n`);
  }

  return base;
};

export const topics = [
  // device
  {
    id: "charging",
    label: "Charging",
    categories: ["device"],
  },
  {
    id: "motor-vibration",
    label: "Motor Vibration",
    categories: ["device"],
  },
  {
    id: "serial-number",
    label: "Serial Number",
    categories: ["device"],
  },
  {
    id: "compatibility",
    label: "Compatibility",
    categories: ["device"],
  },

  // order
  {
    id: "order-tracking-information",
    label: "Order Tracking Information",
    categories: ["order"],
  },
  {
    id: "shipping-information",
    label: "Shipping Information",
    categories: ["order"],
  },
  {
    id: "request-refund",
    label: "Request Refund",
    categories: ["order"],
  },
  {
    id: "repair",
    label: "Repair",
    categories: ["order"],
  },
  {
    id: "cancellation",
    label: "Cancellation",
    categories: ["order"],
  },

  // game-software
  {
    id: "compatible-games",
    label: "Compatible Games",
    categories: ["game-software"],
  },
  {
    id: "game-request",
    label: "Game Request",
    categories: ["game-software"],
  },
  {
    id: "sdk",
    label: "SDK",
    categories: ["game-software"],
  },
  {
    id: "haptic-feedback",
    label: "Haptic Feedback",
    categories: ["game-software"],
  },

  // common
  {
    id: "pair-connect",
    label: "Pair & Connect",
    categories: ["device", "game-software"],
  },
  {
    id: "others",
    label: "Others",
    categories: ["device", "order", "game-software", "others"],
  },
];

export default function TopicSelect() {
  const { t } = useTrans(["support"]);

  const contactUsEmailStorage = useContactUsEmailStorage();
  const form = useFormContext<ContactUsSchemaType>();
  const currentCategory = form.watch("category");
  const options = topics.filter((topic) =>
    topic.categories.some((category) => currentCategory.includes(category)),
  );

  return (
    <FormField
      control={form.control}
      name="topic"
      defaultValue={form.watch("topic")}
      render={({ field }) => (
        <FormItem className="w-full space-y-2">
          <FormLabel variant="select">
            <span>{t("Topic")}</span>
            <span>{" *"}</span>
          </FormLabel>
          <Select
            disabled={!currentCategory}
            onValueChange={(value) => {
              field.onChange(value);
              form.clearErrors("topic");

              if (value === "game-request") {
                form.setValue("description", "");
              } else {
                const newDesc = getDefaultDescription(currentCategory, value);
                form.setValue("description", newDesc);
                form.setValue("game-request", [{ title: "", platform: "" }]);
              }
              contactUsEmailStorage.set(form.getValues());
            }}
            value={field.value}
          >
            <FormControl>
              <SelectTrigger variant="form" ref={field.ref}>
                <SelectValue placeholder={t("Choose a topic")} />
                <ChevronDownIcon />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((topic, index) => (
                <Fragment key={topic.id}>
                  <SelectItem value={topic.id}>{topic.label}</SelectItem>
                  <SelectSeparator
                    className={cn(index === options.length - 1 && "hidden")}
                  />
                </Fragment>
              ))}
            </SelectContent>
          </Select>
          <FormMessage t={t} />
        </FormItem>
      )}
    />
  );
}
